import { Loader, Stack, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'component-library';
import { useTranslation } from 'react-i18next';

import { AvailableCsvExport, CsvReportType } from '@/api/rest/resources/types/reports';
import { SuspenseBoundary } from '@/components';
import { useAvailableCsvExports } from '@/pages/shared/hooks/useReports';

import { CsvExportButton } from './CsvExportButton';

export const DataExportSection = () => {
  const { t } = useTranslation();
  return (
    <Stack spacing={6} className='w-full' data-testid='data-export-section'>
      <Stack spacing={2}>
        <h2 className='typography-h5 text-text-primary'>{t('reports.dataExport.title')}</h2>
        <p className='typography-body2 text-text-secondary'>{t('reports.dataExport.description')}</p>
      </Stack>
      <SuspenseBoundary fallback={<Loader className='min-h-[400px]' />}>
        <DataExportSectionContent />
      </SuspenseBoundary>
    </Stack>
  );
};

const DataExportSectionContent = () => {
  const { t } = useTranslation();
  const { data: availableCsvExports } = useAvailableCsvExports();

  const hasExports = availableCsvExports?.results && availableCsvExports.results.length > 0;

  return hasExports ? (
    <div className='border border-neutral-black-12 rounded-2xl overflow-hidden'>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className='whitespace-nowrap'>{t('reports.dataExport.table.indicatorName')}</TableHead>
            <TableHead className='w-full min-w-[300px] truncate'>
              {t('reports.dataExport.table.indicatorProduct')}
            </TableHead>
            <TableHead className='whitespace-nowrap text-right'>{t('reports.dataExport.table.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {availableCsvExports.results.map((csvExport) => (
            <TableRow key={csvExport.report_type}>
              <TableCell className='font-medium capitalize whitespace-nowrap'>{csvExport.report_name}</TableCell>
              <TableCell>
                <IndicatorProducts csvExport={csvExport} />
              </TableCell>
              <TableCell className='flex justify-end'>
                <CsvExportButton
                  reportType={csvExport.report_type as CsvReportType}
                  reportName={csvExport.report_name}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  ) : (
    <div className='text-center py-8'>
      <p className='typography-body2 text-text-tertiary'>{t('reports.dataExport.noIndicators')}</p>
    </div>
  );
};

const IndicatorProducts = ({ csvExport }: { csvExport: AvailableCsvExport }) => {
  const joinedNames = csvExport.indicator_products.filter(Boolean).join(', ');

  return <div className='text-text-secondary'>{joinedNames}</div>;
};
